import { ShieldCheck } from "lucide-react";
import { HeimdallAttribution } from "@/design/components";
import { useVerificationSummary, useAnalystName } from "@/domain/hooks";

/**
 * The joint assurance a milestone carries before money is released: how many evidence items
 * Heimdall reviewed, how many passed, how many flags were raised and resolved, and the
 * human confirmation. AI-reviewed is always paired with human-confirmed (Heimdall brief §4).
 */
export function VerificationSummaryCard({
  milestoneId,
  reviewerName,
  className,
}: {
  milestoneId: string;
  reviewerName?: string;
  className?: string;
}) {
  // TODO(heimdall): AI analyst not built yet — this whole card is the joint AI+human
  // assurance summary. Hidden until Heimdall ships; remove this early return to restore it.
  return null;

  // eslint-disable-next-line no-unreachable
  const summaryFor = useVerificationSummary();
  const analystName = useAnalystName();
  const s = summaryFor(milestoneId);

  if (s.items === 0 && s.flagsRaised === 0) {
    return (
      <div className={`rounded-md border border-line bg-card px-3 py-2 ${className ?? ""}`}>
        <div className="flex items-center gap-2">
          <HeimdallAttribution name={analystName} firstMention />
          <span className="text-xs text-mute">· no evidence on this milestone yet.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-md border border-line bg-card p-3 ${className ?? ""}`}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-brand" aria-hidden />
        <span className="text-sm font-medium text-ink">Verification</span>
      </div>
      <p className="mt-1.5 text-sm text-mute">
        <span className="tabular-nums text-ink">{s.items}</span> evidence item{s.items === 1 ? "" : "s"} reviewed ·{" "}
        <span className="tabular-nums text-ok">{s.passed}</span> checks passed
        {s.flagsRaised > 0 && (
          <>
            {" "}· <span className="tabular-nums text-warn">{s.flagsRaised}</span> flag{s.flagsRaised === 1 ? "" : "s"} raised
            {s.flagsResolved >= s.flagsRaised ? " and resolved" : ` (${s.flagsResolved} resolved)`}
          </>
        )}
        {s.allConfirmed ? " · all human-confirmed" : " · confirmation in progress"}
      </p>
      <div className="mt-2 border-t border-line pt-2">
        <HeimdallAttribution name={analystName} firstMention reviewerName={reviewerName} />
      </div>
    </div>
  );
}
